import { useEffect, useState } from 'react';
import { adminApi, formatDate } from '../api';

interface Ticket {
  id: string;
  subject: string;
  category: string;
  status: 'open' | 'answered' | 'closed';
  accountId: string;
  email: string;
  userName: string | null;
  databaseId: string | null;
  lastMessageAt: string;
  messages?: {
    id: string;
    author: 'customer' | 'staff';
    authorName: string;
    body: string;
    createdAt: string;
  }[];
}

/** Staff inbox: open tickets first, click to read, reply (optionally closing). Email goes out automatically. */
export function SupportSection({ tick }: { tick: number }) {
  const [filter, setFilter] = useState<'open' | 'answered' | 'closed' | 'all'>('open');
  const [list, setList] = useState<Ticket[] | null>(null);
  const [current, setCurrent] = useState<Ticket | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    adminApi<{ tickets: Ticket[] }>(`/support/tickets${filter === 'all' ? '' : `?status=${filter}`}`)
      .then((d) => setList(d.tickets))
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [tick, filter]);
  const open = async (id: string) => {
    setCurrent((await adminApi<{ ticket: Ticket }>(`/support/tickets/${id}`)).ticket);
    setReply('');
  };
  const send = async (close: boolean) => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi<{ ticket: Ticket }>(`/support/tickets/${current.id}/reply`, {
        method: 'POST',
        json: { body: reply, close },
      });
      setCurrent(r.ticket);
      setReply('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card">
      <div className="row between">
        <h2>
          Support{' '}
          {list && filter === 'open' && list.length > 0 && (
            <span className="badge provisioning">{list.length} waiting</span>
          )}
        </h2>
        <div className="tabs" style={{ margin: 0 }}>
          {(['open', 'answered', 'closed', 'all'] as const).map((f) => (
            <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {f}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="grid cols-2">
        <div>
          {list?.length === 0 && <p className="muted">Nothing here.</p>}
          <table>
            <tbody>
              {list?.map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => void open(t.id)}>
                  <td>
                    <strong>{t.subject}</strong>
                    <div className="muted">
                      {t.category} · {t.userName ?? t.email}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge ${t.status === 'answered' ? 'active' : t.status === 'closed' ? '' : 'provisioning'}`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(t.lastMessageAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          {!current && <p className="muted">Select a ticket.</p>}
          {current && (
            <div>
              <div className="row between">
                <strong>{current.subject}</strong>
                <span className="muted" style={{ fontSize: 12 }}>
                  {current.email} · {current.accountId}
                  {current.databaseId ? ` · ${current.databaseId}` : ''}
                </span>
              </div>
              <div style={{ maxHeight: 360, overflow: 'auto', margin: '10px 0' }}>
                {current.messages?.map((m) => (
                  <div key={m.id} className={`msg ${m.author}`}>
                    <div className="muted" style={{ fontSize: 12 }}>
                      <strong>{m.authorName}</strong> · {formatDate(m.createdAt)}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                  </div>
                ))}
              </div>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={5}
                placeholder="Reply to the customer"
              />
              <div className="row" style={{ marginTop: 8 }}>
                <button disabled={busy || reply.trim().length < 2} onClick={() => void send(false)}>
                  Reply
                </button>
                <button
                  className="secondary"
                  disabled={busy || reply.trim().length < 2}
                  onClick={() => void send(true)}
                >
                  Reply and close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
